<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Teleport to="body">
    <!-- 审批详情弹窗 -->
    <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="fixed inset-0 bg-black/50" @click="closeModal"></div>
      <div class="relative min-h-screen flex items-center justify-center p-4">
        <div class="relative bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
          <!-- 头部 -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
            <h3 class="text-lg font-semibold text-slate-900">{{ title }}</h3>
            <button @click="closeModal" class="p-1.5 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="p-6 flex-1 overflow-y-auto">
            <div class="space-y-4 animate-pulse">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-12 h-12 bg-slate-200 rounded-lg"></div>
                <div class="flex-1">
                  <div class="h-5 w-48 bg-slate-200 rounded mb-2"></div>
                  <div class="h-4 w-32 bg-slate-200 rounded"></div>
                </div>
              </div>
              <div class="grid grid-cols-4 gap-4">
                <div v-for="n in 4" :key="n" class="h-20 bg-slate-200 rounded-lg"></div>
              </div>
              <div class="h-32 bg-slate-200 rounded-lg"></div>
            </div>
          </div>

          <!-- 详情内容 -->
          <div v-else-if="detail" class="p-6 flex-1 overflow-y-auto">
            <!-- 顶部信息 -->
            <div class="flex items-start gap-3 mb-6">
              <div :class="['w-12 h-12 rounded-lg flex items-center justify-center', typeBgClass]">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h4 class="text-xl font-semibold text-slate-900">{{ detail.instance?.title }}</h4>
                <p class="text-sm text-slate-500 mt-1">
                  {{ detail.instance?.applicantName }} | {{ detail.template?.templateName }}
                </p>
              </div>
              <div class="flex flex-col items-end gap-2">
                <span :class="['px-3 py-1 rounded-full text-sm font-medium', priorityClass]">
                  {{ detail.instance?.priorityDesc || priorityText }}
                </span>
                <span :class="['px-2 py-0.5 rounded text-xs', statusClass]">
                  {{ detail.instance?.statusDesc }}
                </span>
              </div>
            </div>

            <!-- 基本信息 -->
            <div class="grid grid-cols-4 gap-3 mb-6">
              <div class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">实例编号</div>
                <div class="text-sm font-medium text-slate-900">{{ detail.instance?.instanceNo || '-' }}</div>
              </div>
              <div class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">当前节点</div>
                <div class="text-sm font-medium text-slate-900">{{ getCurrentNodeDisplayName() || '-' }}</div>
              </div>
              <div class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">申请时间</div>
                <div class="text-sm font-medium text-slate-900">{{ formatDateTime(detail.instance?.createTime) }}</div>
              </div>
              <div class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">结束时间</div>
                <div class="text-sm font-medium text-slate-900">{{ formatDateTime(detail.instance?.endTime) || '-' }}</div>
              </div>
              <div v-if="isApprovalActive()" class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">总等待</div>
                <div class="text-sm font-medium text-slate-900">{{ getTotalWaitingDuration() || '-' }}</div>
              </div>
              <div v-else-if="getTotalElapsedDuration()" class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">总计用时</div>
                <div class="text-sm font-medium text-slate-900">{{ getTotalElapsedDuration() }}</div>
              </div>
              <div v-if="isApprovalActive()" class="bg-slate-50 rounded-lg p-3">
                <div class="text-xs text-slate-500 mb-1">当前节点等待</div>
                <div class="text-sm font-medium text-slate-900">{{ getCurrentNodeWaitingDuration() || '-' }}</div>
              </div>
            </div>

            <!-- 申请说明 -->
            <div class="mb-6">
              <div class="flex items-center justify-between mb-2">
                <h5 class="text-sm font-medium text-slate-700">申请说明</h5>
                <div class="flex items-center gap-3">
                  <!-- 方案审批流程：显示方案详情入口 -->
                  <button
                    v-if="isPlanApprovalFlow && detail.formData?.planId"
                    :disabled="planDetailLoading"
                    class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    @click="loadPlanDetail"
                  >
                    <svg v-if="planDetailLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    查看方案详情
                  </button>
                  <button
                    v-if="isCustomerApprovalFlow && detail.formData?.customerId"
                    class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    @click="loadCustomerDetail"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A7.97 7.97 0 0112 14a7.97 7.97 0 016.879 3.804M15 10a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                    查看客户详情
                  </button>
                  <button
                    v-if="isOrderApprovalFlow && detail.formData?.orderId"
                    :disabled="orderDetailLoading"
                    class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    @click="loadOrderDetail"
                  >
                    <svg v-if="orderDetailLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 14h6m-6-4h6m-7 8h8a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-3.414-3.414A1 1 0 0013.586 4H8a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    查看订单详情
                  </button>
                  <button
                    v-if="isWealthApprovalFlow && wealthApprovalInstanceId"
                    class="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    @click="showWealthModal = true"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    查看资金申请详情
                  </button>
                </div>
              </div>
              <div v-if="detail.instance?.description" class="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
                {{ detail.instance?.description }}
              </div>
            </div>

            <!-- 动态表单数据 -->
            <div v-if="detail.formData && detail.template?.formSchema && formSchemaFields.length" class="mb-6">
              <h5 class="text-sm font-medium text-slate-700 mb-3">申请内容</h5>
              <div class="bg-white border border-slate-200 rounded-lg p-4">
                <div class="grid grid-cols-2 gap-4">
                  <template v-for="field in formSchemaFields" :key="field.key">
                    <div :class="['flex flex-col', field.type === 'TEXTAREA' ? 'col-span-2' : '']">
                      <label class="text-xs text-slate-500 mb-1">{{ field.label }}</label>
                      <!-- 文本 -->
                      <div v-if="['TEXT', 'EMAIL', 'PHONE', 'URL'].includes(field.type)" class="text-sm text-slate-900 font-medium">
                        {{ detail.formData[field.key] || '-' }}
                      </div>
                      <!-- 数字/金额 -->
                      <div v-else-if="['NUMBER', 'MONEY'].includes(field.type)" class="text-sm text-slate-900 font-medium">
                        {{ detail.formData[field.key] != null ? (field.type === 'MONEY' ? '¥' : '') + (typeof detail.formData[field.key] === 'number' ? detail.formData[field.key].toLocaleString() : detail.formData[field.key]) : '-' }}
                      </div>
                      <!-- 文本域 -->
                      <div v-else-if="field.type === 'TEXTAREA'" class="text-sm text-slate-900 whitespace-pre-wrap">
                        {{ detail.formData[field.key] || '-' }}
                      </div>
                      <!-- 日期 -->
                      <div v-else-if="field.type === 'DATE'" class="text-sm text-slate-900 font-medium">
                        {{ detail.formData[field.key] || '-' }}
                      </div>
                      <!-- 日期时间 -->
                      <div v-else-if="field.type === 'DATETIME'" class="text-sm text-slate-900 font-medium">
                        {{ detail.formData[field.key] || '-' }}
                      </div>
                      <!-- 单选/多选 -->
                      <div v-else-if="['SELECT', 'RADIO', 'CHECKBOX'].includes(field.type)" class="text-sm text-slate-900 font-medium">
                        {{ formatSelectValue(detail.formData[field.key], field.options) }}
                      </div>
                      <!-- 开关 -->
                      <div v-else-if="field.type === 'SWITCH'" class="text-sm">
                        <span v-if="detail.formData[field.key]" class="text-green-600">是</span>
                        <span v-else class="text-slate-400">否</span>
                      </div>
                      <!-- 附件 -->
                      <div v-else-if="field.type === 'FILE'" class="text-sm text-slate-600">
                        <FileAttachment
                          v-if="getFormFileList(field.key).length"
                          :files="getFormFileList(field.key)"
                        />
                        <span v-else class="text-slate-400">-</span>
                      </div>
                      <!-- 未知类型 -->
                      <div v-else class="text-sm text-slate-900 font-medium">
                        {{ detail.formData[field.key] || '-' }}
                      </div>
                    </div>
                  </template>
                </div>
                <div v-if="!formSchemaFields.length" class="text-center py-4 text-slate-400 text-sm">
                  暂无表单数据
                </div>
              </div>
            </div>

            <!-- 流程节点轨迹 -->
            <div class="mb-6">
              <div class="flex items-center gap-2 mb-3">
                <svg class="w-4 h-4 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span class="text-sm font-medium text-slate-700">审批流程</span>
                <span class="text-xs text-slate-400">({{ detail.flowNodes?.length || 0 }}个节点)</span>
              </div>

              <div class="pl-2 space-y-2">
                <template v-if="detail.flowNodes?.length">
                  <template v-for="(node, index) in detail.flowNodes" :key="node.nodeId">
                    <div class="relative">
                      <!-- 节点卡片 -->
                      <div class="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-lg" :class="[
                        node.status === 'APPROVED' ? 'border-green-200' : '',
                        node.status === 'REJECTED' ? 'border-red-200' : '',
                        node.status === 'PENDING' ? 'border-amber-200' : ''
                      ]">
                        <!-- 节点序号 -->
                        <div :class="[
                          'w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-medium mt-0.5',
                          node.nodeType === 'CC' ? 'bg-purple-100 text-purple-600' :
                          node.nodeType === 'START' ? 'bg-green-100 text-green-600' :
                          node.nodeType === 'END' ? 'bg-slate-100 text-slate-600' :
                          node.status === 'APPROVED' ? 'bg-green-100 text-green-600' :
                          node.status === 'REJECTED' ? 'bg-red-100 text-red-600' :
                          'bg-blue-100 text-blue-600'
                        ]">
                          <span v-if="node.nodeType === 'START'">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                            </svg>
                          </span>
                          <span v-else-if="node.nodeType === 'END'">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                          </span>
                          <span v-else>{{ index + 1 }}</span>
                        </div>

                        <!-- 节点信息 -->
                        <div class="flex-1 min-w-0">
                          <div class="approval-detail-node-header">
                            <span class="text-sm font-medium text-slate-900">{{ node.nodeName }}</span>
                            <span v-if="node.nodeType === 'CC'" class="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded">抄送</span>
                            <span v-else-if="node.status === 'APPROVED'" class="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs rounded">已通过</span>
                            <span v-else-if="node.status === 'REJECTED'" class="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded">已驳回</span>
                            <span v-else-if="node.status === 'PENDING'" class="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-xs rounded">待审批</span>
                            <span v-else-if="node.nodeType !== 'START' && node.nodeType !== 'END'" class="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">审批</span>
                            <span v-if="getApprovalModeBadgeText(node)" class="approval-detail-mode-tag">
                              {{ getApprovalModeBadgeText(node) }}
                            </span>
                            <span v-if="getNodeApprovedTime(node)" class="approval-detail-node-time">
                              {{ getNodeApprovedTime(node) }}
                            </span>
                          </div>

                          <!-- 审批/抄送参与人 -->
                          <div v-if="node.approvers?.length" class="approval-detail-participants">
                            <div
                              v-for="(approver, idx) in node.approvers"
                              :key="approver.userId || idx"
                              class="approval-detail-participant"
                            >
                              <div class="relative" :title="approver.userName">
                                <div
                                  class="approval-detail-participant-avatar"
                                >
                                  <span v-if="approver.avatar" class="w-full h-full">
                                    <img :src="approver.avatar" class="w-full h-full object-cover" alt="" />
                                  </span>
                                  <span v-else>{{ approver.userName?.charAt(0) || 'U' }}</span>
                                </div>
                                <!-- 状态图标 - 放在头像容器外 -->
                                <span
                                  v-if="approver.actionType"
                                  :class="[
                                    'absolute -top-0.5 -left-0.5 w-2.5 h-2.5 rounded-full flex items-center justify-center ring-1 ring-white',
                                    getActionTypeStyle(approver.actionType).bgClass
                                  ]"
                                  :title="getActionTypeStyle(approver.actionType).text"
                                >
                                  <svg v-if="['AGREE', 'AUTO_AGREE', 'COMPLETED', 'READ'].includes(approver.actionType)" class="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                                  </svg>
                                  <svg v-else-if="['REJECT', 'CANCEL', 'CANCELLED', 'EXPIRED'].includes(approver.actionType)" class="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"/>
                                  </svg>
                                  <svg v-else-if="['DELEGATE'].includes(approver.actionType)" class="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
                                  </svg>
                                  <svg v-else-if="['PENDING'].includes(approver.actionType)" class="w-1.5 h-1.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                  </svg>
                                </span>
                              </div>
                              <div class="min-w-0">
                                <div class="approval-detail-participant-main">
                                  <span class="approval-detail-participant-name">{{ approver.userName }}</span>
                                  <span
                                    v-if="getApproverStatusText(approver, node)"
                                    class="approval-detail-participant-status"
                                    :class="getActionTypeStyle(approver.actionType).textClass"
                                  >
                                    {{ getApproverStatusText(approver, node) }}
                                  </span>
                                </div>
                                <div
                                  v-if="getApproverActionTimeText(approver, node)"
                                  class="approval-detail-participant-time"
                                >
                                  {{ getApproverActionTimeText(approver, node) }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="node.nodeType !== 'START' && node.nodeType !== 'END'" class="flex items-center gap-1.5 mt-1.5">
                            <div class="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                              <svg class="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                              </svg>
                            </div>
                            <span class="text-xs text-slate-400">系统匹配</span>
                          </div>

                          <!-- 审批意见 remark -->
                          <template v-if="node.approvers?.length">
                            <template v-for="(approver, idx) in node.approvers" :key="approver.userId || idx">
                              <div v-if="approver.remark" class="mt-2 p-2 bg-slate-50 rounded border-l-2" :class="approver.action === 'REJECTED' ? 'border-red-400' : approver.action === 'APPROVED' ? 'border-green-400' : 'border-slate-300'">
                                <div class="flex items-center gap-1.5 mb-1">
                                  <span class="text-xs font-medium text-slate-700">{{ approver.userName }}</span>
                                  <span class="text-xs text-slate-400">{{ approver.actionTime }}</span>
                                  <span v-if="approver.action === 'APPROVED'" class="text-xs text-green-600">通过</span>
                                  <span v-else-if="approver.action === 'REJECTED'" class="text-xs text-red-600">驳回</span>
                                </div>
                                <p class="text-xs text-slate-600">{{ approver.remark }}</p>
                              </div>
                            </template>
                          </template>
                        </div>
                      </div>
                      <!-- 箭头连接线 -->
                      <div v-if="index < detail.flowNodes.length - 1" class="absolute left-5 -bottom-3 text-slate-300 z-10">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </div>
                    </div>
                  </template>
                </template>
                <template v-else>
                  <div class="text-center py-6 bg-slate-50 rounded-lg text-xs text-slate-400">
                    暂无审批流程信息
                  </div>
                </template>
              </div>
            </div>

            <!-- 审批意见 - 可编辑 -->
            <div v-if="showCommentEditor">
              <label class="block text-sm font-medium text-slate-700 mb-2">审批意见</label>
              <textarea
                v-model="comment"
                rows="3"
                class="form-textarea"
                placeholder="请输入审批意见（可选）"
              ></textarea>
            </div>
          </div>

          <!-- 空状态 -->
          <div v-else class="p-6 flex-1 overflow-y-auto">
            <div class="text-center py-12 text-slate-400">
              <svg class="w-16 h-16 mx-auto mb-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p>暂无详情数据</p>
            </div>
          </div>

          <!-- 底部按钮 -->
          <div v-if="showActions" class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 shrink-0">
            <button @click="handleReject" class="px-5 py-2.5 border border-red-200 text-red-600 rounded-lg hover:bg-red-50">
              驳回
            </button>
            <button @click="handleApprove" class="px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700">
              通过
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 方案详情弹窗 -->
    <PlanDetailModal
      v-if="isPlanApprovalFlow"
      :visible="showProposalModal"
      :plan-id="detail?.formData?.planId"
      :plan-data="planDetail"
      @update:visible="showProposalModal = $event"
    />
    <CustomerDetailModal
      v-if="isCustomerApprovalFlow"
      v-model="showCustomerModal"
      :customer="customerDetail"
    />
    <OrderDetailModal
      v-if="isOrderApprovalFlow"
      v-model:visible="showOrderModal"
      :order-detail="orderDetail"
      :trace-list="orderTraceList"
    />
    <WealthOperationApplyDetailModal
      v-if="isWealthApprovalFlow"
      v-model="showWealthModal"
      :apply-id="wealthApplyId"
      :approval-instance-id="wealthApprovalInstanceId"
    />
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick, defineAsyncComponent } from 'vue'
import { getInstanceDetail } from '@/api/flow'
import { getPlanDetail } from '@/api/plan'
import { getOrderDetail } from '@/api/order'
import { getTraceList } from '@/api/trace'
import PlanDetailModal from './PlanDetailModal.vue'
import FileAttachment from './FileAttachment.vue'

const CustomerDetailModal = defineAsyncComponent(() => import('./CustomerDetailModal.vue'))
const OrderDetailModal = defineAsyncComponent(() => import('./OrderDetailModal.vue'))
const WealthOperationApplyDetailModal = defineAsyncComponent(() => import('@/views/wealth/components/WealthOperationApplyDetailModal.vue'))

const props = defineProps({
  // 控制显示
  modelValue: {
    type: Boolean,
    default: false
  },
  // 标题
  title: {
    type: String,
    default: '审批详情'
  },
  // 实例ID（传入ID会自动加载详情）
  instanceId: {
    type: [String, Number],
    default: null
  },
  // 直接传入详情数据
  detailData: {
    type: Object,
    default: null
  },
  // 类型（用于图标背景色）
  type: {
    type: String,
    default: 'proposal'
  },
  // 优先级
  priority: {
    type: String,
    default: 'normal'
  },
  // 是否显示操作按钮
  showActions: {
    type: Boolean,
    default: false
  },
  // 是否显示审批意见编辑框
  showCommentEditor: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'approve', 'reject', 'close'])

const loading = ref(false)
const detail = ref(null)
const comment = ref('')
const planDetail = ref(null)
const planDetailLoading = ref(false)
const showProposalModal = ref(false)
const customerDetail = ref(null)
const showCustomerModal = ref(false)
const orderDetail = ref(null)
const orderTraceList = ref([])
const orderDetailLoading = ref(false)
const showOrderModal = ref(false)
const showWealthModal = ref(false)

// 类型背景色映射
const typeBgMap = {
  'proposal': 'bg-blue-100 text-blue-600',
  'customer': 'bg-emerald-100 text-emerald-600',
  'order': 'bg-amber-100 text-amber-600',
  'wealth': 'bg-cyan-100 text-cyan-700',
  'franchisee': 'bg-purple-100 text-purple-600',
  'product': 'bg-green-100 text-green-600',
  'refund': 'bg-amber-100 text-amber-600'
}

const typeBgClass = computed(() => typeBgMap[props.type] || 'bg-slate-100 text-slate-600')

// 优先级样式映射
const priorityMap = {
  'urgent': { text: '紧急', class: 'bg-red-100 text-red-700' },
  'high': { text: '高', class: 'bg-amber-100 text-amber-700' },
  'normal': { text: '普通', class: 'bg-slate-100 text-slate-600' }
}

const priorityText = computed(() => priorityMap[props.priority]?.text || '普通')
const priorityClass = computed(() => priorityMap[props.priority]?.class || 'bg-slate-100 text-slate-600')

// 状态样式
const statusClass = computed(() => {
  if (!detail.value?.instance?.status) return 'bg-slate-100 text-slate-700'
  const status = detail.value.instance.status
  if (status === 'APPROVED') return 'bg-green-100 text-green-700'
  if (status === 'REJECTED') return 'bg-red-100 text-red-700'
  return 'bg-blue-100 text-blue-700'
})

// 解析表单 schema
const formSchemaFields = computed(() => {
  if (!detail.value?.template?.formSchema) return []

  try {
    const schema = typeof detail.value.template.formSchema === 'string'
      ? JSON.parse(detail.value.template.formSchema)
      : detail.value.template.formSchema

    const fields = schema.fields || schema
    return Array.isArray(fields) ? fields : []
  } catch {
    console.error('解析表单 schema 失败')
    return []
  }
})

// 是否为方案审批流程
const isPlanApprovalFlow = computed(() => {
  return detail.value?.template?.templateCode === 'PLAN_APPROVAL_FLOW'
})

const isCustomerApprovalFlow = computed(() => {
  return props.type === 'customer' ||
    detail.value?.template?.templateCode === 'CUSTOMER_REFERRER_CHANGE_FLOW' ||
    detail.value?.instance?.bizType === 'customer_referrer_change_approval'
})

const isOrderApprovalFlow = computed(() => {
  return props.type === 'order' ||
    detail.value?.template?.templateCode === 'ORDER_AMOUNT_CHANGE_FLOW' ||
    detail.value?.instance?.bizType === 'order_amount_change_approval'
})

const isWealthApprovalFlow = computed(() => {
  return props.type === 'wealth' ||
    detail.value?.template?.templateCode === 'WEALTH_FUND_OPERATION_FLOW' ||
    detail.value?.instance?.bizType === 'wealth_fund_operation_approval'
})

const wealthApplyId = computed(() => {
  return detail.value?.formData?.applyId ||
    detail.value?.instance?.bizId ||
    detail.value?.formData?.bizId ||
    null
})

const wealthApprovalInstanceId = computed(() => {
  return props.instanceId ||
    detail.value?.instance?.id ||
    detail.value?.instance?.instanceId ||
    null
})

// 格式化选择值
function formatSelectValue(value, options) {
  if (value === undefined || value === null) return '-'

  if (Array.isArray(value)) {
    if (!value.length) return '-'
    const labels = value.map(v => {
      const opt = options?.find(o => o.value == v || o.value === v)
      return opt?.label || v
    })
    return labels.join('、')
  }

  const opt = options?.find(o => o.value == value || o.value === value)
  return opt?.label || value || '-'
}

function getFormFileList(fieldKey) {
  const value = detail.value?.formData?.[fieldKey]
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

const APPROVED_ACTION_TYPES = ['AGREE', 'AUTO_AGREE', 'COMPLETED']
const FINISHED_APPROVAL_ACTION_TYPES = [...APPROVED_ACTION_TYPES, 'REJECT']

// 审批动作类型样式
function getActionTypeStyle(actionType) {
  const map = {
    'AGREE': { bgClass: 'bg-green-500', textClass: 'text-green-700', text: '已同意' },
    'AUTO_AGREE': { bgClass: 'bg-green-500', textClass: 'text-green-700', text: '自动通过' },
    'COMPLETED': { bgClass: 'bg-green-500', textClass: 'text-green-700', text: '已完成' },
    'READ': { bgClass: 'bg-green-500', textClass: 'text-green-700', text: '已读' },
    'REJECT': { bgClass: 'bg-red-500', textClass: 'text-red-700', text: '已驳回' },
    'CANCEL': { bgClass: 'bg-slate-400', textClass: 'text-slate-500', text: '已取消' },
    'CANCELLED': { bgClass: 'bg-slate-400', textClass: 'text-slate-500', text: '已取消' },
    'EXPIRED': { bgClass: 'bg-amber-500', textClass: 'text-amber-700', text: '已过期' },
    'DELEGATE': { bgClass: 'bg-blue-500', textClass: 'text-blue-700', text: '已转交' },
    'PENDING': { bgClass: 'bg-amber-400', textClass: 'text-amber-700', text: '待审批' }
  }
  return map[actionType] || { bgClass: 'bg-slate-300', textClass: 'text-slate-500', text: actionType }
}

function getApprovalModeBadgeText(node) {
  if (node?.nodeType !== 'APPROVAL') return ''
  if (!node?.approvers?.length || node?.approvers?.length <= 1) return ''
  if (node.approvalMode === 'ALL') return '会签'
  if (node.approvalMode === 'ANY') return '或签'
  return ''
}

function getNodeApprovedTime(node) {
  if (node?.nodeType !== 'APPROVAL' || node?.status !== 'APPROVED') return ''

  const latestTime = (node.approvers || [])
    .filter(approver => APPROVED_ACTION_TYPES.includes(approver?.actionType) && approver?.actionTime)
    .map(approver => new Date(approver.actionTime).getTime())
    .filter(time => Number.isFinite(time))
    .reduce((latest, time) => Math.max(latest, time), 0)

  return latestTime ? formatDateTime(latestTime) : ''
}

function getApproverStatusText(approver, node) {
  if (!approver?.actionType) return ''
  if (node?.nodeType === 'CC' && approver.actionType === 'PENDING') return '未读'
  return approver.actionTypeDesc || getActionTypeStyle(approver.actionType).text
}

function getApproverActionTimeText(approver, node) {
  if (!approver?.actionTime) return ''

  if (node?.nodeType === 'CC') {
    return approver.actionType === 'READ'
      ? formatDateTime(approver.actionTime)
      : ''
  }

  if (!FINISHED_APPROVAL_ACTION_TYPES.includes(approver.actionType)) return ''

  return formatDateTime(approver.actionTime)
}

function isApprovalActive() {
  return detail.value?.instance?.status === 'APPROVING'
}

function getCurrentPendingNode() {
  if (!isApprovalActive()) return null
  return (detail.value?.flowNodes || []).find(node => node?.status === 'PENDING') || null
}

function getCurrentNodeDisplayName() {
  if (!isApprovalActive()) return ''
  return detail.value?.instance?.currentNodeName || getCurrentPendingNode()?.nodeName || ''
}

function getTotalWaitingDuration() {
  if (!isApprovalActive()) return ''
  return formatDurationBetween(detail.value?.instance?.createTime)
}

function getTotalElapsedDuration() {
  const instance = detail.value?.instance
  if (isApprovalActive() || !instance?.createTime || !instance?.endTime) return ''
  return formatDurationBetween(instance.createTime, instance.endTime)
}

function getCurrentNodeWaitingDuration() {
  if (!isApprovalActive()) return ''
  const node = getCurrentPendingNode()
  if (!node?.startTime) return ''
  return formatDurationBetween(node.startTime)
}

function formatDurationBetween(start, end = Date.now()) {
  if (!start) return ''

  const startTime = new Date(start).getTime()
  const endTime = typeof end === 'number' ? end : new Date(end).getTime()
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime < startTime) {
    return ''
  }

  const totalMinutes = Math.floor((endTime - startTime) / 60000)
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const minutes = totalMinutes % 60

  if (days > 0) {
    return `${days}天${hours}小时`
  }
  if (hours > 0) {
    return `${hours}小时${minutes}分钟`
  }
  return `${Math.max(minutes, 1)}分钟`
}

// 格式化时间
function formatDateTime(date) {
  if (!date) return '-'
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 加载详情
async function loadDetail() {
  resetBusinessDetailModals()
  if (props.detailData) {
    detail.value = props.detailData
    return
  }

  if (!props.instanceId) {
    detail.value = null
    return
  }

  loading.value = true
  try {
    const res = await getInstanceDetail(props.instanceId)
    detail.value = res || null
  } catch (error) {
    console.error('加载实例详情失败:', error)
    detail.value = null
  } finally {
    loading.value = false
  }
}

// 加载方案详情
async function loadPlanDetail() {
  const planId = detail.value?.formData?.planId
  if (!planId) return

  planDetailLoading.value = true
  try {
    const res = await getPlanDetail(planId)
    planDetail.value = res || null
    // 先设置数据，再显示弹窗，确保 PlanDetailModal 能获取到
    await nextTick()
    showProposalModal.value = true
  } catch (error) {
    console.error('加载方案详情失败:', error)
  } finally {
    planDetailLoading.value = false
  }
}

async function loadCustomerDetail() {
  const customerId = detail.value?.formData?.customerId
  if (!customerId) return

  customerDetail.value = { id: customerId }
  await nextTick()
  showCustomerModal.value = true
}

async function loadOrderDetail() {
  const orderId = detail.value?.formData?.orderId
  if (!orderId) return

  orderDetailLoading.value = true
  try {
    orderDetail.value = await getOrderDetail(orderId)
    orderTraceList.value = await getTraceList('ORDER', orderId)
    await nextTick()
    showOrderModal.value = true
  } catch (error) {
    console.error('加载订单详情失败:', error)
  } finally {
    orderDetailLoading.value = false
  }
}

// 关闭弹窗
function closeModal() {
  resetBusinessDetailModals()
  emit('update:modelValue', false)
  emit('close')
}

function resetBusinessDetailModals() {
  showProposalModal.value = false
  showCustomerModal.value = false
  showOrderModal.value = false
  showWealthModal.value = false
}

// 通过审批
function handleApprove() {
  emit('approve', {
    comment: comment.value,
    detail: detail.value
  })
}

// 驳回审批
function handleReject() {
  emit('reject', {
    comment: comment.value,
    detail: detail.value
  })
}

// 监听弹窗显示
watch(() => props.modelValue, (val) => {
  if (val) {
    comment.value = ''
    loadDetail()
  }
}, { immediate: true })

// 监听详情数据变化
watch(() => props.detailData, (val) => {
  if (val) {
    detail.value = val
  }
})

// 监听instanceId变化
watch(() => props.instanceId, () => {
  if (props.modelValue && props.instanceId) {
    loadDetail()
  }
})
</script>

<style scoped>
/* 滚动条样式 */
:deep(.el-dialog__body) {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

:deep(.el-dialog__body)::-webkit-scrollbar {
  width: 6px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

:deep(.el-dialog__body)::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.approval-detail-mode-tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}

.approval-detail-node-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.approval-detail-node-time {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  color: #15803d;
  font-size: 12px;
  font-weight: 500;
}

.approval-detail-participants {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.approval-detail-participant {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 168px;
  max-width: 240px;
  padding: 7px 9px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.approval-detail-participant-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid #ffffff;
  border-radius: 9999px;
  background: #dbeafe;
  color: #2563eb;
  font-size: 12px;
  font-weight: 600;
}

.approval-detail-participant-main {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.approval-detail-participant-name {
  overflow: hidden;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.approval-detail-participant-status {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
}

.approval-detail-participant-time {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
}
</style>
